import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerTabsJobFile from "../../components/CustomerTabsJobFile";
import JobFileInvoiceTable from "../../components/Table/JobFileInvoiceTable";
import InvoiceEditModal from "../../components/Modal/InvoiceEditModal";
import AddInvoiceModal from "../../components/Modal/AddInvoiceModal";
import { Icon } from "@iconify/react";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/th";
import { openReceiptReportByPayRow } from "../../../utils/ReportUtils";

moment.locale("th");

/** ป้ายอุปกรณ์ชั่วคราวหลังคืน — ใช้ return_outcome_type จาก DB ถ้ามี; ไม่งั้น parse return_note */
function getTempEquipmentReturnBadge(row) {
  const note =
    typeof row === "string"
      ? row
      : row && typeof row.return_note === "string"
        ? row.return_note
        : "";
  const type =
    typeof row === "object" && row != null && row.return_outcome_type != null
      ? Number(row.return_outcome_type)
      : null;

  if (type === 2) {
    return {
      label: "คืนแล้ว",
      style: { backgroundColor: "#dcfce7", color: "#16a34a" },
      borderClass: "border-emerald-300",
    };
  }
  if (type === 1) {
    return {
      label: "คืนแล้วไม่ครบ",
      style: { backgroundColor: "#ffedd5", color: "#c2410c" },
      borderClass: "border-amber-300",
    };
  }
  if (type === 3) {
    return {
      label: "หาย",
      style: { backgroundColor: "#fee2e2", color: "#b91c1c" },
      borderClass: "border-red-300",
    };
  }

  const legacy = note.includes("[คืนไม่ครบ/ค้างหรือหาย]");
  const hasLost = /(?:^|\n)\[ของหาย\]\n/.test(note);
  const hasPartial = /(?:^|\n)\[คืนไม่ครบ\]\n/.test(note) && !legacy;

  if (legacy) {
    return {
      label: "คืนแล้ว (ค้าง — บันทึกเก่า)",
      style: { backgroundColor: "#ffedd5", color: "#c2410c" },
      borderClass: "border-amber-300",
    };
  }
  if (hasLost && hasPartial) {
    return {
      label: "คืนแล้ว (ของหาย + คืนไม่ครบ)",
      style: { backgroundColor: "#fce7f3", color: "#a21caf" },
      borderClass: "border-fuchsia-400",
    };
  }
  if (hasLost) {
    return {
      label: "คืนแล้ว (ของหาย)",
      style: { backgroundColor: "#fee2e2", color: "#b91c1c" },
      borderClass: "border-red-300",
    };
  }
  if (hasPartial) {
    return {
      label: "คืนแล้ว (คืนไม่ครบ)",
      style: { backgroundColor: "#ffedd5", color: "#c2410c" },
      borderClass: "border-amber-300",
    };
  }
  return {
    label: "คืนแล้ว",
    style: { backgroundColor: "#dcfce7", color: "#16a34a" },
    borderClass: "border-emerald-300",
  };
}

function formatTempDateTime(value) {
  if (!value) return "-";
  const m = moment(value);
  if (!m.isValid()) return "-";
  const buddhistYear = m.year() + 543;
  return `${m.format("DD/MM")}/${buddhistYear} ${m.format("HH:mm")}`;
}

function formatTempDateOnly(value) {
  if (!value) return "-";
  const m = moment(value);
  if (!m.isValid()) return "-";
  const buddhistYear = m.year() + 543;
  return `${m.format("DD/MM")}/${buddhistYear}`;
}

function getTempEquipmentPlacementBadge(row) {
  const returned = Number(row?.status) === 4;
  if (returned) {
    return {
      label: "เก็บคืนแล้ว",
      style: { backgroundColor: "#dcfce7", color: "#166534" },
    };
  }
  return {
    label: "วางอยู่",
    style: { backgroundColor: "#fef3c7", color: "#92400e" },
  };
}

function getTempEquipmentCollectResultBadge(row) {
  const type = row?.return_outcome_type != null ? Number(row.return_outcome_type) : null;
  if (type === 2) {
    return {
      label: "เก็บคืนครบ",
      style: { backgroundColor: "#dcfce7", color: "#16a34a" },
    };
  }
  if (type === 1) {
    return {
      label: "เก็บคืนไม่ครบ",
      style: { backgroundColor: "#ffedd5", color: "#c2410c" },
    };
  }
  if (type === 3) {
    return {
      label: "หาย",
      style: { backgroundColor: "#fee2e2", color: "#b91c1c" },
    };
  }
  const rm = String(row?.collect_close_remark || "").trim();
  if (rm === "เก็บคืน") {
    return {
      label: "เก็บคืนครบ",
      style: { backgroundColor: "#dcfce7", color: "#16a34a" },
    };
  }
  if (rm === "เก็บแล้วไม่ครบ") {
    return {
      label: "เก็บคืนไม่ครบ",
      style: { backgroundColor: "#ffedd5", color: "#c2410c" },
    };
  }
  if (rm === "หาย") {
    return {
      label: "หาย",
      style: { backgroundColor: "#fee2e2", color: "#b91c1c" },
    };
  }
  return null;
}

function normalizeTempItemKey(row) {
  const raw = String(row?.item_summary || row?.wh_doc_no || "").trim();
  if (!raw) return "-";
  // ตัด suffix ปริมาณท้ายข้อความ เช่น "x2", "x2 ตัว" เพื่อรวมสินค้าชนิดเดียวกัน
  const normalized = raw.replace(/\s+x[\d.]+(?:\s+\S+)?$/u, "").trim();
  return normalized || raw;
}

function extractQtyByItemKey(itemSummary, itemKey) {
  if (!itemSummary || !itemKey) return 0;
  const parts = String(itemSummary).split(/[,\n]/);
  let total = 0;
  parts.forEach((part) => {
    const trimmed = part.trim();
    if (!trimmed) return;
    const match = trimmed.match(/^(.+?)\s+x([\d.]+)(?:\s+(.+))?$/);
    const name = match ? match[1].trim() : trimmed;
    if (name !== itemKey) return;
    const qty = match ? Number.parseFloat(match[2]) : 1;
    if (Number.isFinite(qty)) total += qty;
  });
  return total;
}

function resolveCollectType(row) {
  const type = row?.return_outcome_type != null ? Number(row.return_outcome_type) : null;
  if (type === 2) return "full";
  if (type === 1) return "partial";
  if (type === 3) return "lost";
  const rm = String(row?.collect_close_remark || "").trim();
  if (rm === "เก็บคืน") return "full";
  if (rm === "เก็บแล้วไม่ครบ") return "partial";
  if (rm === "หาย") return "lost";
  return isCollectedTempEquipmentRow(row) ? "full" : "none";
}

function getCollectTeamName(row, collectType) {
  if (collectType === "none") return "";
  return String(
    row?.collect_team_name ||
    row?.return_team_name ||
    row?.team_name ||
    row?.van_wh_name ||
    row?.return_by ||
    ""
  ).trim();
}

function getTempEquipmentEventTime(row) {
  const candidates = [
    row?.return_date,
    row?.updated_at,
    row?.modified_at,
    row?.updated_date,
    row?.collect_at,
    row?.checkout_at,
    row?.when_placed,
    row?.created_at,
  ];
  for (const value of candidates) {
    if (!value) continue;
    const ts = new Date(value).getTime();
    if (!Number.isNaN(ts)) return ts;
  }
  return 0;
}

function isCollectedTempEquipmentRow(row) {
  if (!row || typeof row !== "object") return false;
  if (Number(row.status) === 4) return true;
  if (row.return_outcome_type != null) return true;
  const collectRemark = String(row.collect_close_remark || "").trim();
  if (collectRemark === "เก็บคืน" || collectRemark === "เก็บแล้วไม่ครบ" || collectRemark === "หาย") {
    return true;
  }
  const returnNote = String(row.return_note || "").trim();
  return returnNote.length > 0;
}

export default function JobFileDetailPage() {
  const { contractNo } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceEditModal, setShowInvoiceEditModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [invoiceDataForAdd, setInvoiceDataForAdd] = useState(null);
  const [tempEquipment, setTempEquipment] = useState([]);

  const job = location.state?.job || null;
  const fromStockHistory = location.state?.fromStockHistory === true;
  const stockOverviewLink = location.state?.stockOverviewLink || "/stock/stock-overview";
  const stockOverviewLabel = location.state?.stockOverviewLabel || "ดูสต๊อกสินค้า";
  const stockHistoryBackLink = location.state?.backLink || "/stock/stock-detail";
  const stockHistoryBackLabel = location.state?.backLabel || "ประวัติการเคลื่อนไหวสินค้า";
  const fromTempEquipment = location.state?.fromTempEquipment === true;
  const tempEquipmentBackLink = location.state?.backLink || "/temp-equipment";
  const genericBackLink = location.state?.backLink || "/jobfile";
  const genericBackLabel = location.state?.backLabel || "แฟ้มงาน";

  const mergedTempEquipment = useMemo(() => {
    const map = new Map();
    tempEquipment.forEach((row) => {
      const key = normalizeTempItemKey(row);
      const existing = map.get(key);
      if (!existing) {
        const rowQty = extractQtyByItemKey(row.item_summary, key);
        const collectType = resolveCollectType(row);
        const placeTeam = String(row.team_name || row.van_wh_name || "").trim();
        const collectTeam = getCollectTeamName(row, collectType);
        map.set(key, {
          ...row,
          __itemKey: key,
          __latestEventTime: getTempEquipmentEventTime(row),
          __isCollectedLatest: isCollectedTempEquipmentRow(row),
          __qtyPlaced: collectType === "none" ? rowQty : 0,
          __qtyCollected: collectType === "full" || collectType === "partial" ? rowQty : 0,
          __qtyLost: collectType === "lost" ? rowQty : 0,
          __hasPartialCollect: collectType === "partial",
          __placeTeams: placeTeam ? [placeTeam] : [],
          __collectTeams: collectTeam ? [collectTeam] : [],
        });
        return;
      }

      const rowQty = extractQtyByItemKey(row.item_summary, key);
      const collectType = resolveCollectType(row);
      if (collectType === "none") {
        existing.__qtyPlaced = Number(existing.__qtyPlaced || 0) + rowQty;
      }
      if (collectType === "lost") {
        existing.__qtyLost = Number(existing.__qtyLost || 0) + rowQty;
      } else if (collectType === "full" || collectType === "partial") {
        existing.__qtyCollected = Number(existing.__qtyCollected || 0) + rowQty;
      }
      if (collectType === "partial") existing.__hasPartialCollect = true;

      // ใช้สถานะ "ล่าสุด" ตามเวลาเหตุการณ์จริง
      const rowEventTime = getTempEquipmentEventTime(row);
      const existingEventTime = existing.__latestEventTime || 0;
      const rowIsCollected = isCollectedTempEquipmentRow(row);
      const existingIsCollected = Boolean(existing.__isCollectedLatest);
      const shouldReplaceStatus =
        rowEventTime > existingEventTime ||
        (rowIsCollected && !existingIsCollected) ||
        (rowEventTime === existingEventTime && rowIsCollected && !existingIsCollected) ||
        (existingEventTime === 0 && rowEventTime === 0 && rowIsCollected && !existingIsCollected);
      if (shouldReplaceStatus) {
        existing.status = row.status;
        existing.status_label = row.status_label;
        existing.__latestEventTime = rowEventTime;
        existing.__isCollectedLatest = rowIsCollected;
        existing.return_outcome_type = row.return_outcome_type;
        existing.collect_close_remark = row.collect_close_remark;
        existing.return_note = row.return_note;
      }

      // วันที่วางเอาที่เก่าสุด
      const exPlaced = existing.checkout_at || existing.when_placed;
      const rowPlaced = row.checkout_at || row.when_placed;
      if (!exPlaced || (rowPlaced && rowPlaced < exPlaced)) {
        existing.checkout_at = row.checkout_at;
        existing.when_placed = row.when_placed;
      }
      if (!existing.when_end || (row.when_end && row.when_end > existing.when_end)) {
        existing.when_end = row.when_end;
      }

      // รวมทีมวาง/ทีมเก็บคืนแยกคอลัมน์
      const placeTeam = String(row.team_name || row.van_wh_name || "").trim();
      const collectTeam = getCollectTeamName(row, collectType);
      const placeSet = new Set([...(existing.__placeTeams || []), ...(placeTeam ? [placeTeam] : [])]);
      const collectSet = new Set([...(existing.__collectTeams || []), ...(collectTeam ? [collectTeam] : [])]);
      existing.__placeTeams = Array.from(placeSet);
      existing.__collectTeams = Array.from(collectSet);
      existing.team_name = existing.__placeTeams.join(", ");
      existing.return_by = existing.__collectTeams.join(", ");

      // เก็บ label ที่อ่านง่ายสำหรับแสดงสินค้า
      existing.item_summary = key;
    });
    return Array.from(map.values());
  }, [tempEquipment]);

  const breadcrumbs = useMemo(() => {
    const docLabel = data?.Contract_No || contractNo || "";
    const contractViewId = data?.Contract_Id || contractNo;
    const contractCrumb =
      docLabel && contractViewId
        ? [{ label: docLabel, link: `/contract/view/${contractViewId}` }]
        : docLabel
          ? [{ label: docLabel }]
          : [];
    if (fromStockHistory) {
      return [
        { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
        { label: stockOverviewLabel, link: stockOverviewLink },
        { label: stockHistoryBackLabel, link: stockHistoryBackLink },
        ...contractCrumb,
      ];
    }
    if (fromTempEquipment) {
      return [
        { label: "งานช่าง", link: "/job-management" },
        { label: "รายการอุปกรณ์ชั่วคราว", link: tempEquipmentBackLink },
        ...contractCrumb,
      ];
    }
    const jobFileLink =
      genericBackLink && String(genericBackLink).startsWith("/jobfile")
        ? genericBackLink
        : "/jobfile";
    const extraBackCrumb =
      genericBackLink &&
      !String(genericBackLink).startsWith("/jobfile") &&
      genericBackLabel !== "แฟ้มงาน"
        ? [{ label: genericBackLabel, link: genericBackLink }]
        : [];
    return [
      { label: "งานช่าง", link: "/job-management" },
      { label: "แฟ้มงาน", link: jobFileLink },
      ...extraBackCrumb,
      ...contractCrumb,
    ];
  }, [fromStockHistory, stockOverviewLabel, stockOverviewLink, stockHistoryBackLabel, stockHistoryBackLink, fromTempEquipment, tempEquipmentBackLink, genericBackLink, genericBackLabel, data?.Contract_No, data?.Contract_Id, contractNo]);

  useEffect(() => {
    fetchContractData();
  }, [contractNo]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      const contractId = contractNo; // contractNo คือ Doc_Id
      
      // ดึงข้อมูลสัญญา
      const contractRes = await api.get(`/get-contract/${contractId}`);
      if (contractRes.data.status !== "OK") {
        throw new Error(contractRes.data.message || "ไม่สามารถดึงข้อมูลสัญญาได้");
      }

      const header = contractRes.data.header || {};
      
      // ดึงข้อมูลสถานที่บริการ (ServiceAddr) เพื่อเอาข้อมูลสเปค
      let serviceAddrInfo = {};
      if (header.ServiceAddr_Id) {
        try {
          const serviceAddrRes = await api.get(`/get-tb-set-service-addr-by-id/${header.ServiceAddr_Id}`);
          if (serviceAddrRes.data.status === "OK" && serviceAddrRes.data.result?.recordset?.[0]) {
            serviceAddrInfo = serviceAddrRes.data.result.recordset[0];
          }
        } catch (err) {
          console.error("Error fetching service address info:", err);
        }
      }
      
      // ดึงข้อมูลลูกค้าเพิ่มเติม
      let customerInfo = {};
      let titles = [];
      
      // ดึง titles
      try {
        const titleRes = await api.get("/get-title");
        titles = titleRes.data?.result || [];
      } catch (err) {
        console.error("Error fetching titles:", err);
      }

      if (header.AR_Id) {
        try {
          const custRes = await api.get(`/get-tb-set-ar1/${header.AR_Id}`);
          const customerData = custRes.data?.result?.recordset || [];
          if (customerData.length > 0) {
            customerInfo = customerData[0];
          }
        } catch (err) {
          console.error("Error fetching customer info:", err);
        }
      }

      // สร้างชื่อลูกค้าแบบเต็ม
      const titleObj = titles.find(
        (t) => Number(t.Title_Id) === Number(customerInfo.AR_TitleCode)
      );
      const title = titleObj ? titleObj.Title_Name : "";
      const name = customerInfo.AR_Name || header.AR_Name || "";
      const lastName = customerInfo.AR_LastName || header.AR_LastName || "";
      const fullName = `${title ? title + " " : ""}${name}${lastName ? " " + lastName : ""}`.trim() || "-";

      // สร้างที่อยู่สำหรับสถานที่บริการ
      const serviceLocationParts = [
        header.AddrNo,
        header.Village,
        header.Moo ? `หมู่ ${header.Moo}` : null,
        header.Soi ? `ซอย ${header.Soi}` : null,
        header.Road ? `ถนน ${header.Road}` : null,
      ].filter(Boolean);

      // Format เลขผู้เสียภาษี
      const formatTaxNo = (taxNo) => {
        if (!taxNo) return "-";
        return taxNo.replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/, "$1-$2-$3-$4-$5");
      };

      // Format วันที่หมดสัญญา
      const formatContractEndDate = (date) => {
        if (!date) return "-";
        return moment(date).add(543, "year").format("MMMM YY");
      };

      // Format ราคาคู่แข่ง
      const formatCompetitorPrice = (price) => {
        if (!price || isNaN(price)) return "-";
        return parseFloat(price).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      };

      // กำหนดประเภทลูกค้า
      const arGroup = Number(customerInfo.AR_Group) || Number(header.AR_Group) || null;
      const customerType = arGroup === 1 ? "บุคคลธรรมดา" : arGroup === 2 ? "นิติบุคคล" : "-";

      // Format ข้อมูลสเปคจากสถานที่บริการ (เหมือน LocationModal)
      const formatServiceSpec = () => {
        const specParts = [
          serviceAddrInfo.Width ? `กว้าง ${serviceAddrInfo.Width} เมตร` : null,
          serviceAddrInfo.Length ? `ยาว ${serviceAddrInfo.Length} เมตร` : null,
          serviceAddrInfo.Height ? `สูง ${serviceAddrInfo.Height} ชั้น` : null,
          serviceAddrInfo.SpecRemark ? `(${serviceAddrInfo.SpecRemark})` : null,
        ].filter(Boolean);
        
        return specParts.length > 0 ? specParts.join(" , ") : "-";
      };

      const formattedData = {
        Contract_No: header.Doc_No || contractNo,
        Contract_Id: header.Doc_Id || contractId,
        CustomerName: fullName,
        DisplayName: customerInfo.DisplayName || header.DisplayName || "-",
        CustomerType: customerType,
        TaxNo: formatTaxNo(customerInfo.AR_TaxNo || header.AR_TaxNo),
        ContractEndDate: formatContractEndDate(header.Contract_EndDate || customerInfo.Contract_EndDate),
        ServiceName: header.Service_Name || "-",
        SaleEmpName: header.Sale_Emp_Name || "-",
        Quote_No: header.Ref_DocNo || "-",
        Quote_Id: header.Ref_DocID || null,
        ServiceLocation: {
          no: serviceLocationParts.join(" ") || "-",
          sub: header.SubDistrictName || "-",
          district: header.DistrictName || "-",
          province: header.ProvinceName || "-",
          zip: header.Zipcode || "-",
        },
        ServiceSpec: formatServiceSpec(),
        ServiceRemark: header.Service_Remark || "-",
        QuoteDetail: header.Quote_Detail || "-",
        QuoteRemark: header.Quote_Remark || "-",
        AddrRemark: serviceAddrInfo.AddrRemark || "-",
      };

      setData(formattedData);

      // ดึงข้อมูล invoice จาก CT_Pay
      const invoiceRes = await api.get(`/get-contract-invoices/${contractId}`);
      if (invoiceRes.data.status === "OK") {
        const invoiceList = (invoiceRes.data.result || []).map((inv) => {
          // กำหนดสถานะ
          let status = "pending";
          if (inv.Invoice_Status === 1) {
            status = "paid";
          } else if (inv.Invoice_Status === 2) {
            status = "overdue";
          } else {
            // ตรวจสอบว่าเกินกำหนดหรือไม่ (ถ้า CT_Due_Date ผ่านมาแล้วและยังไม่ชำระ)
            if (inv.CT_Due_Date) {
              const payDate = new Date(inv.CT_Due_Date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (payDate < today && inv.Invoice_Status === 0) {
                status = "overdue";
              }
            }
          }

          return {
            CT_Pay_Id: inv.CT_Pay_Id,
            Doc_Id: inv.Doc_Id, // เพิ่ม Doc_Id เพื่อส่งไปสร้าง invoice ใหม่
            Invoice_No: inv.Invoice_No || "-",
            Line: inv.Line,
            Date: inv.CT_Due_Date 
              ? new Date(inv.CT_Due_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            CT_Due_Date: inv.CT_Due_Date, // เพิ่ม CT_Due_Date ต้นฉบับเพื่อใช้ใน invoice ใหม่
            Due_Date: inv.Due_Date || null, // เพิ่ม Due_Date
            Total: inv.CT_Due_Amount || 0,
            ToPay: inv.CT_Due_Amount || 0,
            Paid: inv.Paid_Amount || 0,
            PayDate: inv.Paid_Date
              ? new Date(inv.Paid_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Remark: inv.Remark || "-",
            Ref1: inv.Ref1 || "-",
            Ref2: inv.Ref2 || "-",
            Payment_Method:
              inv.Payment_Method != null && inv.Payment_Method !== ""
                ? String(inv.Payment_Method)
                : "1",
            Withholding_Tax:
              inv.Withholding_Tax === 1 || inv.Withholding_Tax === true,
            Bank:
              inv.Bank && String(inv.Bank).trim() !== "" ? inv.Bank : "-",
            Check_No:
              inv.Check_No && inv.Check_No !== "" ? inv.Check_No : "-",
            Check_Date: inv.Check_Date
              ? new Date(inv.Check_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Transfer_Bank:
              inv.Transfer_Bank && String(inv.Transfer_Bank).trim() !== ""
                ? inv.Transfer_Bank
                : "-",
            Transfer_Account:
              inv.Transfer_Account && inv.Transfer_Account !== ""
                ? inv.Transfer_Account
                : "-",
            Card_Bank:
              inv.Card_Bank && String(inv.Card_Bank).trim() !== ""
                ? inv.Card_Bank
                : "-",
            Card_No: inv.Card_No && inv.Card_No !== "" ? inv.Card_No : "-",
            Status: status,
            Invoice_Status: inv.Invoice_Status || 0,
            Is_VAT: inv.Is_VAT === 1 || inv.Is_VAT === true,
          };
        });

        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }

      try {
        const teRes = await api.get(`/app/admin/temp-equipment?contract_id=${contractId}`);
        if (teRes.data?.status === "OK") setTempEquipment(teRes.data.result || []);
        else setTempEquipment([]);
      } catch {
        setTempEquipment([]);
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถดึงข้อมูลใบแจ้งหนี้ได้",
      });
      setInvoices([]);
      setTempEquipment([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceData = async () => {
    try {
      const contractId = contractNo;
      const invoiceRes = await api.get(`/get-contract-invoices/${contractId}`);
      if (invoiceRes.data.status === "OK") {
        const invoiceList = (invoiceRes.data.result || []).map((inv) => {
          // กำหนดสถานะ
          let status = "pending";
          if (inv.Invoice_Status === 1) {
            status = "paid";
          } else if (inv.Invoice_Status === 2) {
            status = "overdue";
          } else {
            // ตรวจสอบว่าเกินกำหนดหรือไม่ (ถ้า CT_Due_Date ผ่านมาแล้วและยังไม่ชำระ)
            if (inv.CT_Due_Date) {
              const payDate = new Date(inv.CT_Due_Date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (payDate < today && inv.Invoice_Status === 0) {
                status = "overdue";
              }
            }
          }

          return {
            CT_Pay_Id: inv.CT_Pay_Id,
            Doc_Id: inv.Doc_Id, // เพิ่ม Doc_Id เพื่อส่งไปสร้าง invoice ใหม่
            Invoice_No: inv.Invoice_No || "-",
            Line: inv.Line,
            Date: inv.CT_Due_Date 
              ? new Date(inv.CT_Due_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            CT_Due_Date: inv.CT_Due_Date, // เพิ่ม CT_Due_Date ต้นฉบับเพื่อใช้ใน invoice ใหม่
            Due_Date: inv.Due_Date || null, // เพิ่ม Due_Date
            Total: inv.CT_Due_Amount || 0,
            ToPay: inv.CT_Due_Amount || 0,
            Paid: inv.Paid_Amount || 0,
            PayDate: inv.Paid_Date
              ? new Date(inv.Paid_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Remark: inv.Remark || "-",
            Ref1: inv.Ref1 || "-",
            Ref2: inv.Ref2 || "-",
            Payment_Method:
              inv.Payment_Method != null && inv.Payment_Method !== ""
                ? String(inv.Payment_Method)
                : "1",
            Withholding_Tax:
              inv.Withholding_Tax === 1 || inv.Withholding_Tax === true,
            Bank:
              inv.Bank && String(inv.Bank).trim() !== "" ? inv.Bank : "-",
            Check_No:
              inv.Check_No && inv.Check_No !== "" ? inv.Check_No : "-",
            Check_Date: inv.Check_Date
              ? new Date(inv.Check_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Transfer_Bank:
              inv.Transfer_Bank && String(inv.Transfer_Bank).trim() !== ""
                ? inv.Transfer_Bank
                : "-",
            Transfer_Account:
              inv.Transfer_Account && inv.Transfer_Account !== ""
                ? inv.Transfer_Account
                : "-",
            Card_Bank:
              inv.Card_Bank && String(inv.Card_Bank).trim() !== ""
                ? inv.Card_Bank
                : "-",
            Card_No: inv.Card_No && inv.Card_No !== "" ? inv.Card_No : "-",
            Status: status,
            Invoice_Status: inv.Invoice_Status || 0,
            Is_VAT: inv.Is_VAT === 1 || inv.Is_VAT === true,
          };
        });

        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      setInvoices([]);
    }
  };

  const handleAddInvoice = (invoiceData) => {
    console.log('handleAddInvoice ถูกเรียก', invoiceData);
    setInvoiceDataForAdd(invoiceData);
    setShowAddInvoiceModal(true);
  };

  const handlePrintInvoice = (row) => {
    openReceiptReportByPayRow(row);
  };

  const handleConfirmAddInvoice = async (formData) => {
    try {
      // ใช้ Doc_Id จาก invoice เดิม หรือดึงจาก contract
      let docId = invoiceDataForAdd.Doc_Id;
      if (!docId) {
        const contractRes = await api.get(`/get-contract/${contractNo}`);
        if (contractRes.data.status !== "OK") {
          throw new Error("ไม่สามารถดึงข้อมูลสัญญาได้");
        }
        const header = contractRes.data.header || {};
        docId = header.Doc_Id || contractNo;
      }

      // เรียก API สร้าง Invoice ใหม่
      const createRes = await api.post("/create-contract-invoice", {
        Doc_Id: docId,
        remainingAmount: invoiceDataForAdd.remainingAmount,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        sourceInvoice: {
          Invoice_No: invoiceDataForAdd.Invoice_No,
          Line: invoiceDataForAdd.Line,
          CT_Due_Date: invoiceDataForAdd.CT_Due_Date,
        },
      });

      if (createRes.data.status !== "OK") {
        throw new Error(createRes.data.error || "สร้าง Invoice ใหม่ไม่สำเร็จ");
      }

      // รีเฟรชข้อมูล
      fetchInvoiceData();
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  };

  if (loading || !data) {
    return (
      <div className="flex">
        <Sidebar />
        <div
          className="flex-1 min-h-screen bg-gray-50"
          style={{ marginLeft: "var(--sidebar-width)" }}
        >
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />

      <div
        className="flex-1 min-h-screen bg-gray-50"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        <Topbar breadcrumbs={breadcrumbs} />

        {/* --- CUSTOMER TABS (STICKY) --- */}
        <div className="sticky top-14 z-20 bg-white pl-0 pr-6">
          <CustomerTabsJobFile
            job={{ Contract_No: data?.Contract_No || job?.Contract_No }}
            className="pl-2 pr-6 py-6"
          />
        </div>

        {/* {tempEquipment.length > 0 && (() => {
          const activeTemp = tempEquipment.filter((t) => t.status !== 4);
          return (
            <div className="px-8 pt-4">
              <div
                className={`rounded-lg border p-4 ${
                  activeTemp.length ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"
                }`}
              >
                <h3 className="text-sm font-bold text-[#E13F57] mb-1">อุปกรณ์ชั่วคราว (วางบ้านลูกค้า)</h3>
                <p className="text-sm text-gray-700">
                  ทั้งหมด {tempEquipment.length} รายการ
                  {activeTemp.length > 0 && (
                    <span className="text-amber-900 font-medium ml-2">
                      — ยังไม่คืน {activeTemp.length} รายการ
                    </span>
                  )}
                </p>
                <ul className="mt-2 text-xs text-gray-700 space-y-1 max-h-36 overflow-y-auto">
                  {tempEquipment.slice(0, 10).map((t) => (
                    <li key={t.wh_doc_temp_id}>
                      <span className="font-medium">{t.wh_doc_no || "-"}</span>: {t.status_label}
                      {t.warning ? <span className="text-amber-800"> — {t.warning}</span> : null}
                      {t.van_wh_name ? <span className="text-gray-500"> ({t.van_wh_name})</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()} */}

        {/* --- TITLE --- */}
        <div className="pl-2 pr-8 pt-3 pb-4">
          <h1 className="text-xl font-bold text-gray-900">
            {data.Contract_No}
          </h1>
        </div>

        {/* --- CONTENT --- */}
        <div className="pl-2 pr-8 grid grid-cols-12 gap-6 pb-20">

          {/* CARD 1 - ข้อมูลลูกค้า */}
          <div className="col-span-12 md:col-span-4 bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-[15px] font-bold mb-4 text-[#E13F57]">
              ข้อมูลลูกค้า / บริษัท
            </h2>

            <div className="space-y-1 text-[14px]">
              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">ชื่อลูกค้า/บริษัท</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.CustomerName || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">ชื่อที่ใช้ในสัญญา</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.DisplayName || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">ประเภทลูกค้า</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.CustomerType || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">เลขผู้เสียภาษี</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.TaxNo || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">วันที่หมดสัญญา</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.ContractEndDate || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">ใบเสนอราคา</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                {data.Quote_Id ? (
                  <Link
                    to={`/quote/edit/${data.Quote_Id}`}
                    state={{
                      backLink: location.pathname + location.search,
                      backLabel: data.Contract_No || "แฟ้มงาน",
                      fromJobFile: true,
                      jobFileBackLink: genericBackLink || "/jobfile",
                      jobFileContractLabel: data.Contract_No || contractNo || "รายละเอียดแฟ้มงาน",
                      jobFileContractLink: location.pathname + location.search,
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.Quote_No || "-"}
                  </Link>
                ) : (
                  <span className="text-gray-800">{data.Quote_No || "-"}</span>
                )}
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">สัญญา</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                {data.Contract_Id ? (
                  <Link
                    to={`/contract/edit/${data.Contract_Id}`}
                    state={{
                      fromJobFile: true,
                      jobFileBackLink: location.pathname + location.search,
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {data.Contract_No || "-"}
                  </Link>
                ) : (
                  <span className="text-gray-800">{data.Contract_No || "-"}</span>
                )}
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">บริการ</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.ServiceName || "-"}</span>
              </div>

              <div className="flex">
                <span className="w-40 font-bold text-gray-700 flex-shrink-0">พนักงานขาย</span>
                <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                <span className="text-gray-800">{data.SaleEmpName || "-"}</span>
              </div>

            </div>
          </div>

          {/* CARD 2 - สถานที่บริการ */}
          <div className="col-span-12 md:col-span-8 bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[15px] font-bold text-[#E13F57]">
                สถานที่บริการ
              </h2>

              <button className="text-sm flex items-center gap-1 text-gray-700 hover:text-black">
                <Icon icon="mdi:google-maps" className="w-4 h-4" />
                Google Maps
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ซ้าย - ที่อยู่ */}
              <div>
                <h3 className="text-[14px] font-bold text-gray-700 mb-2"></h3>
                <div className="space-y-2 text-[14px]">
                  <Row label="เลขที่/ซอย/ถนน" value={data.ServiceLocation.no} />
                  <Row label="ตำบล/แขวง" value={data.ServiceLocation.sub} />
                  <Row label="อำเภอ/เขต" value={data.ServiceLocation.district} />
                  <Row label="จังหวัด" value={data.ServiceLocation.province} />
                  <Row label="รหัสไปรษณีย์" value={data.ServiceLocation.zip} />
                </div>
              </div>

              {/* ขวา - ข้อมูลสเปคและหมายเหตุ */}
              <div className="space-y-4">
                {/* ข้อมูลสเปค */}
                <div>
                  <h3 className="text-[14px] font-bold text-gray-700 mb-2"></h3>
                  <div className="text-[14px]">
                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700 flex-shrink-0">ข้อมูลสเปค</span>
                      <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                      <span className="text-gray-800">{data.ServiceSpec || "-"}</span>
                    </div>
                  </div>
                </div>

                {/* หมายเหตุ */}
                <div>
                  <h3 className="text-[14px] font-bold text-gray-700 mb-2"></h3>
                  <div className="text-[14px] space-y-2">
                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700 flex-shrink-0">หมายเหตุบริการ</span>
                      <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                      <span className="text-gray-800 whitespace-pre-wrap">
                        {data.ServiceRemark && data.ServiceRemark !== "-" ? data.ServiceRemark : "-"}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="w-40 font-bold text-gray-700 flex-shrink-0">หมายเหตุ</span>
                      <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
                      <span className="text-gray-800 whitespace-pre-wrap">
                        {data.QuoteRemark && data.QuoteRemark !== "-" ? data.QuoteRemark : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {fromTempEquipment && tempEquipment.length > 0 && (
            <div className="col-span-12">
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h2 className="text-[15px] font-bold mb-3 text-[#E13F57]">
                  ข้อมูลจากรายการอุปกรณ์ชั่วคราว
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-[14px] border table-fixed">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700">
                        <th className="border px-2 py-2 text-center w-14">#</th>
                        <th className="border px-2 py-2 text-center w-[38%]">สินค้า/อุปกรณ์</th>
                        <th className="border px-2 py-2 text-center w-28">วาง</th>
                        <th className="border px-2 py-2 text-center w-28">เก็บคืนได้</th>
                        <th className="border px-2 py-2 text-center w-24">หาย</th>
                        <th className="border px-2 py-2 text-center w-44">วันที่วาง</th>
                        <th className="border px-2 py-2 text-center w-36">วันที่ครบกำหนด</th>
                        <th className="border px-2 py-2 text-center w-52">ทีมที่วาง</th>
                        <th className="border px-2 py-2 text-center w-52">ทีมที่เก็บคืน</th>
                        <th className="border px-2 py-2 text-center w-36">สถานะ</th>
                        <th className="border px-2 py-2 text-center w-44">ผลเก็บคืน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mergedTempEquipment.map((t, idx) => {
                        const placementBadge = getTempEquipmentPlacementBadge(t);
                        const collectBadge =
                          Number(t.status) === 4
                            ? getTempEquipmentCollectResultBadge(t) || getTempEquipmentReturnBadge(t)
                            : null;
                        return (
                          <tr key={t.__itemKey || t.wh_doc_temp_id || idx} className="odd:bg-white even:bg-gray-50">
                            <td className="border px-2 py-2 text-center">{idx + 1}</td>
                            <td className="border px-2 py-2 break-words whitespace-normal">
                              {t.item_summary || t.wh_doc_no || "-"}
                            </td>
                            <td className="border px-2 py-2 text-center">{Number(t.__qtyPlaced || 0).toLocaleString()}</td>
                            <td className="border px-2 py-2 text-center">
                              {Number(t.__qtyCollected || 0).toLocaleString()}
                              {t.__hasPartialCollect ? <span className="text-amber-700 text-xs ml-1">(ไม่ครบ)</span> : null}
                            </td>
                            <td className="border px-2 py-2 text-center">
                              {Number(t.__qtyLost || 0) > 0 ? (
                                <span className="text-red-700 font-semibold">{Number(t.__qtyLost || 0).toLocaleString()}</span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="border px-2 py-2 text-center">
                              {formatTempDateTime(t.checkout_at || t.when_placed)}
                            </td>
                            <td className="border px-2 py-2 text-center">
                              {formatTempDateOnly(t.when_end)}
                            </td>
                            <td className="border px-2 py-2">
                              {t.team_name || t.van_wh_name || "-"}
                            </td>
                            <td className="border px-2 py-2">
                              {t.return_by || "-"}
                            </td>
                            <td className="border px-2 py-2 text-center">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                                style={placementBadge.style}
                              >
                                {placementBadge.label}
                              </span>
                            </td>
                            <td className="border px-2 py-2 text-center">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                                style={collectBadge?.style || { backgroundColor: "#f3f4f6", color: "#6b7280" }}
                              >
                                {collectBadge?.label || "-"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TABLE - ใบแจ้งหนี้ */}
          <div className="col-span-12">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h2 className="text-[15px] font-bold mb-3 text-[#E13F57]">ข้อมูลใบแจ้งหนี้</h2>
              <JobFileInvoiceTable
                invoices={invoices}
                onEdit={(row) => {
                  setSelectedInvoice(row);
                  setShowInvoiceEditModal(true);
                }}
                onAddInvoice={handleAddInvoice}
                onPrint={handlePrintInvoice}
                onDelete={async (row) => {
                  const result = await Swal.fire({
                    title: "ยืนยันการลบ",
                    text: `คุณต้องการลบใบแจ้งหนี้ ${row.Invoice_No} ใช่หรือไม่?`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "ลบ",
                    cancelButtonText: "ยกเลิก",
                  });

                  if (result.isConfirmed) {
                    try {
                      const deleteRes = await api.delete(`/delete-contract-invoice/${row.CT_Pay_Id}`);
                      if (deleteRes.data.status !== "OK") {
                        throw new Error(deleteRes.data.error || "ลบใบแจ้งหนี้ไม่สำเร็จ");
                      }

                      fetchInvoiceData();
                    } catch (error) {
                      console.error("Error deleting invoice:", error);
                      Swal.fire({
                        icon: "error",
                        title: "เกิดข้อผิดพลาด",
                        text: error.message || "ไม่สามารถลบใบแจ้งหนี้ได้",
                      });
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Edit Modal */}
      <InvoiceEditModal
        isOpen={showInvoiceEditModal}
        onClose={() => {
          setShowInvoiceEditModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onSuccess={() => {
          fetchInvoiceData();
        }}
      />

      {/* ADD INVOICE MODAL */}
      {showAddInvoiceModal && invoiceDataForAdd && (
        <AddInvoiceModal
          isOpen={showAddInvoiceModal}
          onClose={() => {
            setShowAddInvoiceModal(false);
            setInvoiceDataForAdd(null);
          }}
          invoiceData={invoiceDataForAdd}
          onConfirm={handleConfirmAddInvoice}
        />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex text-gray-700">
      <span className="w-40 font-bold text-gray-700 flex-shrink-0">{label}</span>
      <span className="mr-4 font-bold text-gray-700 flex-shrink-0">:</span>
      <span className="text-gray-800">{value || "-"}</span>
    </div>
  );
}
